import { t } from '../i18n.js';
import { gameServerUrl } from '../net-config.js';
import { session } from '../session.js';
import { отказСловами } from './lobby-data.js';

/**
 * Форма входа: вкладки, вход, регистрация. И больше ничего.
 *
 * ⚠️ ЭТОТ ФАЙЛ НЕ ЗНАЕТ ПРО ИГРУ, И В ЭТОМ ВЕСЬ СМЫСЛ. Он живёт до движка:
 * пока человек набирает пароль, Babylon ещё не скачан. Прежде вход лежал
 * внутри net/auth-flow.ts вместе со всей связкой игры — сцена, снасть, узел
 * подписок, — и потому тянул за собой 8.5 МБ движка на экран, которому нужны
 * форма да словарь.
 *
 * Что дальше делать с полученным ключом, решает вызывающий: это может быть
 * лобби, а может — сразу игра.
 */

export interface Вошедший {
  token: string;
  email: string;
  role: string;
  /**
   * Имя, под которым игрока видят остальные.
   *
   * ⚠️ ПОЧТА ОСТАЁТСЯ, НО ТОЛЬКО ДЛЯ ХОЗЯИНА. Показывать её на экране больше
   * нельзя нигде: она приватная. Здесь она нужна лишь затем, чтобы человек
   * в настройках видел, под какой учётной записью сидит.
   */
  name: string;
  /**
   * Гость: учётка без пароля, живущая только по токену в этом браузере.
   *
   * ⚠️ НУЖЕН ДВУМ МЕСТАМ, И ОБА ВАЖНЫЕ: предупреждение при выходе (гостю
   * возвращаться нечем) и предложение завести почту с паролем в настройках.
   */
  guest?: boolean;
}

/**
 * Связать форму входа. Возвращает то, что нужно знать снаружи.
 *
 * @param onSuccess зовётся с ключом и ролью после удачного входа
 */
export function bindLoginForm(onSuccess: (кто: Вошедший) => void): void {
  const tabLogin = document.getElementById('auth-tab-login');
  const tabRegister = document.getElementById('auth-tab-register');
  const formLogin = document.getElementById('auth-form-login') as HTMLFormElement | null;
  const formRegister = document.getElementById('auth-form-register') as HTMLFormElement | null;
  if (tabLogin === null || tabRegister === null || formLogin === null || formRegister === null) {
    return;
  }

  tabLogin.addEventListener('click', () => {
    tabLogin.classList.add('active');
    tabRegister.classList.remove('active');
    formLogin.classList.add('active');
    formRegister.classList.remove('active');
  });
  tabRegister.addEventListener('click', () => {
    tabRegister.classList.add('active');
    tabLogin.classList.remove('active');
    formRegister.classList.add('active');
    formLogin.classList.remove('active');
  });

  const принять = (data: {
    token: string;
    user: { email: string; role?: string; name?: string; guest?: boolean };
  }): void => {
    session.token = data.token;
    localStorage.setItem('if-auth-token', data.token);
    localStorage.setItem('if-auth-email', data.user.email);
    onSuccess({
      token: data.token,
      email: data.user.email,
      role: data.user.role ?? 'player',
      name: data.user.name ?? data.user.email.split('@')[0],
      guest: data.user.guest === true,
    });
  };

  /**
   * Гостевой вход.
   *
   * ⚠️ КНОПКА ГАСИТСЯ НА ВРЕМЯ ЗАПРОСА. Учётка заводится на каждое нажатие, и
   * нетерпеливый двойной щелчок оставил бы человеку два аккаунта, из которых
   * он попадёт во второй, а прогресс начнёт копить... тоже во втором. Первый
   * же просто займёт место в пределе «три в час».
   */
  document.getElementById('auth-guest')?.addEventListener('click', async () => {
    const кнопка = document.getElementById('auth-guest') as HTMLButtonElement;
    const errEl = document.getElementById('auth-login-error');
    if (errEl !== null) errEl.textContent = '';
    кнопка.disabled = true;
    try {
      const res = await fetch(gameServerUrl('/auth/guest'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = (await res.json()) as {
        token?: string;
        user?: { email: string; role?: string; name?: string; guest?: boolean };
        error?: string;
        key?: string;
      };
      if (!res.ok || data.token === undefined || data.user === undefined) {
        const слово = data.key === undefined ? undefined : t(data.key);
        if (errEl !== null) {
          errEl.textContent =
            слово !== undefined && слово !== data.key ? слово : (data.error ?? t('err.no_answer'));
        }
        return;
      }
      принять({ token: data.token, user: data.user });
    } catch {
      if (errEl !== null) errEl.textContent = t('err.no_answer');
    } finally {
      кнопка.disabled = false;
    }
  });

  formLogin.addEventListener('submit', async (e) => {
    e.preventDefault();
    const errEl = document.getElementById('auth-login-error');
    if (errEl !== null) errEl.textContent = '';
    const email = (document.getElementById('auth-login-email') as HTMLInputElement).value;
    const password = (document.getElementById('auth-login-pass') as HTMLInputElement).value;
    try {
      const res = await fetch(gameServerUrl('/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(отказСловами(data));
      принять(data);
    } catch (err) {
      if (errEl !== null) errEl.textContent = err instanceof Error ? err.message : String(err);
    }
  });

  formRegister.addEventListener('submit', async (e) => {
    e.preventDefault();
    const errEl = document.getElementById('auth-reg-error');
    if (errEl !== null) errEl.textContent = '';
    const name = (document.getElementById('auth-reg-name') as HTMLInputElement).value;
    const email = (document.getElementById('auth-reg-email') as HTMLInputElement).value;
    const password = (document.getElementById('auth-reg-pass') as HTMLInputElement).value;
    const pass2 = (document.getElementById('auth-reg-pass2') as HTMLInputElement).value;
    // ⚠️ ЭТОТ ОТКАЗ — ЕДИНСТВЕННЫЙ СВОЙ, ОСТАЛЬНЫЕ ПРИХОДЯТ С СЕРВЕРА. Те
    // разворачивает отказСловами по ключу; этот сервер не увидит вовсе, потому
    // что до него дело не доходит, — и перевести его больше негде.
    //
    // ⚠️ КЛЮЧ ОБЩИЙ С ЛОББИ, И ЭТО НАМЕРЕННО. Ровно та же жалоба выдаётся при
    // смене пароля в настройках (ui/lobby.ts). Слова там и здесь обязаны
    // совпадать, а два ключа с одинаковым текстом расходятся сами собой —
    // достаточно поправить один и не вспомнить про второй.
    if (password !== pass2) {
      if (errEl !== null) errEl.textContent = t('err.pass_mismatch');
      return;
    }
    try {
      const res = await fetch(gameServerUrl('/auth/register'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(отказСловами(data));
      принять(data);
    } catch (err) {
      if (errEl !== null) errEl.textContent = err instanceof Error ? err.message : String(err);
    }
  });
}

/**
 * Войти по сохранённому ключу, если он ещё годен.
 *
 * ⚠️ НЕГОДНЫЙ КЛЮЧ СТИРАЕТСЯ СРАЗУ. Иначе страница молча пыталась бы войти
 * протухшим ключом при каждом запуске, а человек видел бы форму и не понимал,
 * почему его не помнят.
 */
export async function loginByStoredToken(): Promise<Вошедший | null> {
  const token = localStorage.getItem('if-auth-token');
  if (token === null || token === '') return null;
  try {
    // ⚠️ ПОЧТУ НЕ ШЛЁМ — ОНА ВНУТРИ КЛЮЧА. Прежняя ручка принимала почту и
    // ключ по отдельности, то есть клиент сообщал серверу, кем себя считать,
    // а сервер лишь сверял одно с другим.
    const res = await fetch(gameServerUrl('/auth/check'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    const data = await res.json();
    if (res.ok && data.user) {
      session.token = token;
      const email = data.user.email ?? '';
      return {
        token,
        email,
        role: data.user.role ?? 'player',
        // ⚠️ ЗАПАСНОЙ ВАРИАНТ НУЖЕН ДЛЯ СТАРЫХ УЧЁТНЫХ ЗАПИСЕЙ. Они заведены
        // до того, как имя стали спрашивать, и профиля у них нет. Сервер
        // отдаёт им ту же часть почты до собачки — повторяем её и здесь, на
        // случай если ответ придёт совсем без поля.
        name: data.user.name ?? email.split('@')[0],
        // ⚠️ И ЗДЕСЬ ТОЖЕ. Постоянный игрок проходит форму раз в месяц, а сюда
        // попадает каждый запуск: не возьми мы признак гостя отсюда, кнопка
        // «завести почту» пропадала бы у него при первой же перезагрузке.
        guest: data.user.guest === true,
      };
    }
  } catch {
    // Сервер не отвечает — показываем форму, дальше решит человек.
    return null;
  }
  localStorage.removeItem('if-auth-token');
  localStorage.removeItem('if-auth-email');
  session.token = null;
  return null;
}
